import React, { useCallback, useEffect, useState } from "react";
import { Modal, Drawer, Spin, Button, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import HtmlReportWidgets from "../../views/ReportHtmlView/HtmlReportWidgets";
import ReportIcon from "../../assets/icons/report-dash-icon.svg";
import DownloadIcon from "../../assets/icons/downlode.svg";
import "./DrillDownTarget.css";

const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;

/**
 * Renders a drill-down target report inside a Modal or a Side Panel.
 *
 * The target board is fetched locally on purpose - getBoardByIdAsync writes to
 * boards.selectedBoard, which belongs to the board underneath, and reusing it
 * would wipe out the view the user drilled down from.
 */
export default function DrillDownTarget({ open, mode, boardId, params, onClose }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open || !boardId) return undefined;

    let cancelled = false;
    setLoading(true);
    setBoard(null);
    setReportReady(false);

    fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/dashboard-details/${boardId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
    })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.data) {
          setBoard(data.data);
          // a board with no widgets never reports itself ready
          if (!data.data.resources?.length) setReportReady(true);
        } else {
          message.error("Drill-down report could not be loaded");
        }
      })
      .catch(() => {
        if (!cancelled) message.error("Drill-down report could not be loaded");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, boardId]);

  // if a resource request fails the report never reports itself ready, so give up waiting
  useEffect(() => {
    if (!board || reportReady) return undefined;
    const timer = setTimeout(() => setReportReady(true), 20000);
    return () => clearTimeout(timer);
  }, [board, reportReady]);

  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/generate-pdf-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ id: boardId }),
      });
      if (!response.ok) throw new Error("export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${board?.name || "report"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Could not export the PDF");
    } finally {
      setExporting(false);
    }
  }, [boardId, board]);

  const title = (
    <div className="drilldown-target-title">
      <span className="drilldown-target-icon">
        <img src={ReportIcon} alt="report" />
      </span>
      <span className="drilldown-target-name">{board?.name || "Drill Down"}</span>
    </div>
  );

  // the report is mounted as soon as the board arrives so it can start fetching its
  // own data, but stays hidden behind the spinner until it has something to draw -
  // otherwise there is a blank gap between "loaded the board" and "rendered the report"
  const isBusy = loading || (board && !reportReady);

  const body = (
    <>
      {/* the values carried over from the clicked row */}
      {params?.length && !isBusy ? (
        <div className="drilldown-target-context">
          {params.map((p) => (
            <span className="drilldown-target-chip" key={`ddp_${p.key}`}>
              <b>{p.key}</b>
              {`: ${p.value ?? ""}`}
            </span>
          ))}
        </div>
      ) : null}

      {/* The report must stay VISIBLE while it loads: react-grid-layout measures its
          container to lay the widgets out, and a hidden container measures 0 - which
          left every widget mis-positioned. So the spinner covers it instead. */}
      <div className="drilldown-target-report">
        {isBusy ? (
          <div className="drilldown-target-overlay">
            <Spin tip="Loading report..." />
          </div>
        ) : null}

        {board ? (
          <HtmlReportWidgets
            board={board}
            selectedResource={null}
            bodyParams={null}
            drillDownParams={params || []}
            onReportReady={() => setReportReady(true)}
          />
        ) : !loading ? (
          <div className="drilldown-target-loading">No data</div>
        ) : null}
      </div>
    </>
  );

  const footer = (
    <div className="drilldown-target-footer">
      <Button className="drilldown-target-export" onClick={handleExportPdf} loading={exporting}>
        <img src={DownloadIcon} alt="export" /> Export PDF
      </Button>
      <Button type="primary" className="drilldown-target-close" onClick={onClose}>
        Close
      </Button>
    </div>
  );

  if (mode === "side-panel") {
    return (
      <Drawer
        visible={open}
        onClose={onClose}
        placement="right"
        width={620}
        title={title}
        closeIcon={<CloseOutlined />}
        className="drilldown-target drilldown-target--panel"
        footer={footer}
        destroyOnClose
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      visible={open}
      onCancel={onClose}
      title={title}
      width={880}
      footer={footer}
      className="drilldown-target drilldown-target--modal"
      destroyOnClose
    >
      {body}
    </Modal>
  );
}
